import React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { theme } from 'antd'
import { PiClock, PiSparkle } from 'react-icons/pi'
import { radius, spacing } from '@vacationtracker/shared/constants/ThemeSetting'

import { RecordCardAction, RecordCardGrid, RecordCardLink, RecordCardShell } from '../../design-system/RecordCard'
import { StatusBadge } from '../../design-system/StatusBadge'
import { Text } from '../../design-system/Text'
import { FrontendUrls } from '../../types/urls'

const CATALOG_CARD_HEIGHT = 116
const CATALOG_CARD_MIN_WIDTH = 420

const LOCK_KEY = {
  complete: 'addonsPaymentStatus.availableOnCompletePlan',
  coreAndComplete: 'addonsPaymentStatus.availableOnCoreAndCompletePlan',
} as const

const STATUS = {
  active: { key: 'app.active', tone: 'success' },
  setupRequired: { key: 'catalogCard.setupRequired', tone: 'warning' },
  inactive: { key: 'app.inactive', tone: 'neutral' },
} as const

// 500, not the DS "medium" (600), as on the Locations card: at 16px over a 12px line the size carries the rank.
const NAME_STYLE: React.CSSProperties = { fontWeight: 500, letterSpacing: '-0.3px' }

// Keeps the name on the same line whether or not a row-mate has a badge.
const TITLE_ROW = 24

export interface CatalogCardProps {
  title: React.ReactNode
  logo?: React.ReactNode
  description: React.ReactNode
  fact?: string
  status?: keyof typeof STATUS
  comingSoon?: boolean
  beta?: boolean
  adminsOnly?: boolean
  lock?: 'complete' | 'coreAndComplete' | null
  onSeePlans?: () => void
  href?: string
  external?: boolean
  onSelect?: () => void
  notify?: React.ReactNode
}

const iconWord = (icon: React.ReactNode, word: React.ReactNode) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: spacing.xxs }}>
    {icon}
    {word}
  </span>
)

function LogoSlot({ children }: { children: React.ReactNode }): React.ReactElement {
  const { token } = theme.useToken()
  // The colour is for a catalogue icon standing in for a logo; a brand mark carries its own fill.
  return (
    <span aria-hidden style={{ display: 'inline-flex', flex: 'none', width: 20, height: 20, color: token.colorTextSecondary }}>
      {children}
    </span>
  )
}

function UpgradeLine({ lock, onSeePlans }: { lock: keyof typeof LOCK_KEY; onSeePlans?: () => void }): React.ReactElement {
  const { token } = theme.useToken()
  const { formatMessage } = useIntl()
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap', marginTop: spacing.xxs }}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: spacing.xxs,
          padding: `${spacing.xxs}px ${spacing.xs}px`,
          borderRadius: radius.full,
          // Fades to the card rather than a deeper violet: the deeper end measured 3.2:1 under the text in light.
          background: `linear-gradient(90deg, ${token.colorPrimaryBg}, ${token.colorBgContainer})`,
          border: `1px solid ${token.colorPrimaryBorder}`,
          color: token.colorPrimaryText,
          fontSize: token.fontSizeSM,
          fontWeight: 500,
          whiteSpace: 'nowrap',
        }}
      >
        <PiSparkle aria-hidden />
        {formatMessage({ id: LOCK_KEY[lock] })}
      </span>
      {onSeePlans && (
        <RecordCardAction>
          <Link to={FrontendUrls.billing} onClick={onSeePlans} style={{ color: token.colorPrimaryText, fontSize: token.fontSizeSM, fontWeight: 500 }}>
            {formatMessage({ id: 'catalogCard.seePlans' })}
          </Link>
        </RecordCardAction>
      )}
    </span>
  )
}

function CardTitle({ title, href, external, onSelect }: Pick<CatalogCardProps, 'title' | 'href' | 'external' | 'onSelect'>): React.ReactElement {
  const { formatMessage } = useIntl()

  if (href && external) {
    return (
      <RecordCardLink href={href} external newTabLabel={formatMessage({ id: 'catalogCard.opensInNewTab' })} onClick={onSelect}>
        {title}
      </RecordCardLink>
    )
  }
  if (href) {
    return (
      <RecordCardLink to={href} onClick={onSelect}>
        {title}
      </RecordCardLink>
    )
  }
  if (onSelect) {
    return <RecordCardLink onClick={onSelect}>{title}</RecordCardLink>
  }
  return <>{title}</>
}

/** One catalogue item (an integration or an add-on) as a Locations-style card. */
export function CatalogCard(props: CatalogCardProps): React.ReactElement {
  const { title, logo, description, fact, status, comingSoon, beta, adminsOnly, lock, onSeePlans, href, external, onSelect, notify } = props
  const { token } = theme.useToken()
  const { formatMessage } = useIntl()
  const actionable = !adminsOnly && !lock && Boolean(href || onSelect)

  const stateBadge = lock ? null : comingSoon ? (
    // The clock makes Coming soon read as "later", not as something to do.
    <StatusBadge tone="neutral">{iconWord(<PiClock aria-hidden />, formatMessage({ id: 'app.comingSoon' }))}</StatusBadge>
  ) : (
    status && <StatusBadge tone={STATUS[status].tone}>{formatMessage({ id: STATUS[status].key })}</StatusBadge>
  )

  return (
    // Stretched so every card in a grid row is as tall as the tallest and the bottom lines line up.
    <div style={{ alignSelf: 'stretch', display: 'grid' }}>
      <RecordCardShell height={CATALOG_CARD_HEIGHT}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: spacing.xs, minWidth: 0, minHeight: TITLE_ROW }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap', minWidth: 0 }}>
              {logo && <LogoSlot>{logo}</LogoSlot>}
              <Text size="lg" tone={adminsOnly ? 'tertiary' : 'default'} style={NAME_STYLE}>
                {actionable ? <CardTitle title={title} href={href} external={external} onSelect={onSelect} /> : title}
              </Text>
              {stateBadge}
              {beta && <StatusBadge tone="neutral">{formatMessage({ id: 'catalogCard.beta' })}</StatusBadge>}
            </div>
            {notify}
          </div>
          <Text tone="secondary" size="sm" style={{ whiteSpace: 'normal' }}>
            {description}
            {/* Inline rather than a second line: the description fills the line, so a separate fact wrapped and led its line with the dot. */}
            {fact && (
              <span style={{ whiteSpace: 'nowrap' }}>
                <span aria-hidden style={{ color: token.colorTextQuaternary, margin: `0 ${spacing.xxs}px 0 ${spacing.xs}px` }}>
                  ·
                </span>
                {fact}
              </span>
            )}
          </Text>
          {adminsOnly && (
            <Text tone="secondary" size="sm">
              {formatMessage({ id: 'catalogCard.adminsOnly' })}
            </Text>
          )}
          {lock && <UpgradeLine lock={lock} onSeePlans={onSeePlans} />}
        </div>
      </RecordCardShell>
    </div>
  )
}

export function CatalogCardGrid({ children }: { children: React.ReactNode }): React.ReactElement {
  return <RecordCardGrid minWidth={CATALOG_CARD_MIN_WIDTH}>{children}</RecordCardGrid>
}

export default CatalogCard
